import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from '../../../../hooks/useClient';
import useScreenSize from '../../../../hooks/useScreenSize';
import { prettyNumber } from '../../../../utils/helpers';
import {
  ApartmentInfo,
  ApartmentInfoArea,
  ApartmentInfoPrice,
  ApartmentPricePerMeter,
  ApartmentPriceValue,
  BottomApartmentInfo,
  BottomApartmentInfoText,
  DotDivider
} from './styled';
import { Apartment } from '../../../../graphql/graphql';

interface ApartmentFilterInfoProps {
  item: Apartment;
  isModalView?: boolean;
}

export const ApartmentFilterInfo: React.FC<ApartmentFilterInfoProps> = ({
  item,
  isModalView = false
}) => {
  const { t } = useTranslation();
  const isClient = useClient();
  const { isSmallOrLargeDesktop } = useScreenSize();

  const totalPrice = prettyNumber(Math.round(item.area * item.priceFrom));
  const pricePerMeter = prettyNumber(item.priceFrom);
  const { currency } = item;
  const shouldRenderOnDesktop = isSmallOrLargeDesktop || !isClient;

  const bottomInfo = useMemo(
    () =>
      [
        {
          title: t('apartment_repair'),
          value: item.repair?.name,
          isTitleShown: shouldRenderOnDesktop
        },
        {
          title: t('apartment_bathroom_count'),
          value: item.bathroomsCount
        },
        {
          title: t('apartment_balcon_count'),
          value: item.balconiesCount
        }
      ].filter(info => info.value),
    [
      t,
      item.repair?.name,
      item.balconiesCount,
      item.bathroomsCount,
      shouldRenderOnDesktop
    ]
  );

  return (
    <ApartmentInfo $isModalView={isModalView}>
      <ApartmentInfoArea
        tag="title_4_new"
        $isModalView={isModalView}
        data-cy="apartments-name"
      >
        <span>
          {t('rooms.rooms_count', {
            count: item.rooms,
            postProcess: 'interval'
          })}
        </span>
        <span>{t('area_metres', { area: item.area })}</span>
      </ApartmentInfoArea>

      <ApartmentInfoPrice>
        <ApartmentPriceValue tag="body_2" data-cy="apartment-price">
          {t('apartment_price', { price: totalPrice, currency })}
        </ApartmentPriceValue>
        <ApartmentPricePerMeter tag="body_2">
          {t('apartment_price_per_meter', { price: pricePerMeter, currency })}
        </ApartmentPricePerMeter>
      </ApartmentInfoPrice>

      <BottomApartmentInfo>
        {bottomInfo.map(({ value, title, isTitleShown = true }, index) => (
          <React.Fragment key={title}>
            {index > 0 && <DotDivider />}
            <BottomApartmentInfoText tag="body_2" $isTitleShown={isTitleShown}>
              {isTitleShown && <span>{title}</span>}
              <span>{value}</span>
            </BottomApartmentInfoText>
          </React.Fragment>
        ))}
      </BottomApartmentInfo>
    </ApartmentInfo>
  );
};
